import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Media from 'sb-media'
import ValueLink from 'sb-valuelink'
import reduxForm from 'sb-redux-form'
import { createSelector } from 'reselect'
import { connect } from 'redaction'
import actions from 'redux/actions'
import { mixpanel, snowplow, getMainPageLink, constants, compose } from 'common/helpers'
import { required, streetAddress, zipCode } from 'helpers/validation'

import injectGlobals from 'decorators/injectGlobals'

import Box from 'components/_new/layout/Box/Box'
import Button from 'components/_new/Button/Button'
import Text from 'components/_new/typography/Text/Text'
import VisuallyHidden from 'components/ui/VisuallyHidden/VisuallyHidden'
import PayPalPaymentButton from 'components/payment/PayPalPaymentButton/PayPalPaymentButton'
import RadioGroup from 'components/forms/RadioGroup/RadioGroup'

import AddressRadioOption from './AddressRadioOption/AddressRadioOption'
import ErrorRadioOption from './ErrorRadioOption/ErrorRadioOption'

import messages from './messages'
import s from './AddressesForm.scss'


const trimAddressValues = ({ street, apt, city, state, zipCode }) => ({ street, apt, city, state, zipCode })

const getFormOptions = ({ recommendValues, enteredValues }) => ({
  name: 'AddressesForm',
  fields: {
    entered: {
      fields: {
        street: [],
        apt: [],
        city: [],
        state: [],
        zipCode: [],
      },
    },
    recommend: {
      fields: {
        street: [ required, streetAddress ],
        apt: [],
        city: [ required ],
        state: [ required ],
        zipCode: [ required, zipCode ],
      },
    },
  },
  initialValues: () => ({
    entered: {
      ...trimAddressValues(enteredValues),
    },
    recommend: {
      ...trimAddressValues(recommendValues),
    },
  }),
})

class AddressesForm extends Component {

  static propTypes = {
    recommendValues: PropTypes.shape({
      street: PropTypes.string,
      apt: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zipCode: PropTypes.string,
    }),
    enteredValues: PropTypes.shape({
      street: PropTypes.string,
      apt: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zipCode: PropTypes.string,
    }),
    paymentType: PropTypes.string,
    mainPageLink: PropTypes.string,
    paymentMethod: PropTypes.string,
    payPalBeginAction: PropTypes.func.isRequired,
    payPalCommitAction: PropTypes.func.isRequired,
    payPalOnSuccess: PropTypes.func,
    placement: PropTypes.string,
  }

  constructor({ recommendValues, enteredValues }) {
    super()

    this.state = {
      activeOption: 'recommend',
      options: [
        {
          name: 'entered',
          eventType: constants.addressVerificationOptions.youEntered,
          label: messages.firstLabel,
          dataTestId: constants.testIds.payment.enteredAddressOption,
        },
        {
          name: 'recommend',
          eventType: constants.addressVerificationOptions.weRecommend,
          label: messages.secondLabel,
          hasError: recommendValues.isFailed, // TODO refactor this - do more clearly
          dataTestId: constants.testIds.payment.recommendAddressOption,
        },
      ],
      editableFields: this.getEditableFields(trimAddressValues(recommendValues), enteredValues),
    }
  }

  getEditableFields = (recommendValues, enteredValues) => {
    const editableFields = []

    Object.keys(recommendValues).forEach((field) => {
      const recommendValue  = recommendValues[field] && recommendValues[field].trim().toLowerCase()
      const enteredValue    = enteredValues[field] && enteredValues[field].trim().toLowerCase()

      if (recommendValue !== enteredValue) {
        editableFields.push(field)
      }
    })

    return editableFields
  }

  submit = (values, eventType) => {
    const { onSubmit } = this.props

    mixpanel.track(constants.analyticsEvents.addressValidationPopup.click, {
      type: eventType,
    })

    onSubmit({
      values,
      eventType,
    })
  }

  submitForm = (eventType) => {
    const { activeOption } = this.state
    const { submitForm, enteredValues } = this.props

    if (activeOption === 'recommend') {
      return submitForm((values) => (
        this.submit(values[activeOption], eventType)
      ))()
    }

    this.submit(trimAddressValues(enteredValues), eventType)
  }

  handleClickUse = () => {
    const { paymentMethod } = this.props

    snowplow.track(constants.snowplowEvents.addressValidationPopup.useAddress, {
      paymentMethod,
    }, false)

    this.submitForm(constants.addressVerificationOptions.useThisAddress)
  }

  handleClickEdit = () => {
    const { paymentMethod } = this.props

    snowplow.track(constants.snowplowEvents.addressValidationPopup.editAddress, {
      paymentMethod,
    }, false)

    this.submitForm(constants.addressVerificationOptions.editThisAddress)
  }

  handleClickUsePayPal = () =>
    new Promise((resolve, reject) => {
      const { activeOption } = this.state
      const {
        paymentType, values, plan, submitForm, enteredValues, placement, mainPageLink,
        giftSubscriptionCookie, giftCardCookie, isRegisterGqlEnabled,
      } = this.props

      submitForm(async (newValues) => {
        const newAddress = {
          ...enteredValues,
          ...newValues[activeOption],
        }

        const data = {
          paymentMethod: constants.paymentMethods.payPal,
          // TODO this is so weird to see this here
          plan,
        }

        if (placement) {
          data.placement = placement
        }

        // TODO ductape for digitals GiftCard and GiftSubscription - there is no shipping
        if (!values.shipping) {
          data.billing = newAddress
        }
        else {
          data.shipping = newAddress
          data.billing = values.billing || newAddress
        }

        if (giftSubscriptionCookie) {
          data.giftSubscription = giftSubscriptionCookie
        }

        if (giftCardCookie) {
          data.giftCard = giftCardCookie
        }

        if (values.user) {
          actions.models.ui.showRequestLoader()

          try {
            await actions.models.user.register({ values: values.user, isRegisterGqlEnabled })
          }
          catch (err) {
            actions.models.ui.hideRequestLoader()
            reject(err)
            return
          }

          if (paymentType === constants.paymentTypes.subscription) {
            actions.models.subscription.fetch()
              .then(({ isSubscribed }) => {
                if (isSubscribed) {
                  actions.models.router.push(mainPageLink)
                }
                else {
                  resolve(data)
                }
              })
              .finally(() => actions.models.ui.hideRequestLoader())
          }
          else {
            actions.models.ui.hideRequestLoader()
            resolve(data)
          }
        }
        else {
          resolve(data)
        }
      })()
    })

  render() {
    const { activeOption, options, editableFields } = this.state
    const {
      fields, paymentType, paymentMethod, payPalBeginAction, payPalCommitAction, payPalOnSuccess,
      isEnoughCreditsToBuyEcommerce, isEnoughVindiciaCreditsToBuy,
    } = this.props

    const activeOptionLink = ValueLink.state(this, 'activeOption')

    const isPayPalPayment = (
      paymentMethod === constants.paymentMethods.payPal
      && !isEnoughCreditsToBuyEcommerce
      && !isEnoughVindiciaCreditsToBuy
    )

    return (
      <Box mt="16">
        <RadioGroup name="validateOptions" valueLink={activeOptionLink} ariaLabelledBy="optsSharedLabel">
          <VisuallyHidden>
            <Text id="optsSharedLabel" message={messages.sharedLabel} />
          </VisuallyHidden>
          {
            options.map((option) => {
              const isActive = option.name === activeOption

              return option.hasError ? (
                <ErrorRadioOption
                  key={option.name}
                  activeOption={activeOption}
                  activeOptionLink={activeOptionLink}
                  isActive={isActive}
                  onEdit={this.handleClickEdit}
                  ariaLabelledBy="optsSharedLabel"
                  {...option}
                />
              ) : (
                <AddressRadioOption
                  key={option.name}
                  activeOption={activeOption}
                  activeOptionLink={activeOptionLink}
                  valueLink={fields[option.name]}
                  isActive={isActive}
                  editableFields={editableFields}
                  ariaLabelledBy="optsSharedLabel"
                  {...option}
                />
              )
            })
          }
        </RadioGroup>
        <Box mt="24">
          {
            isPayPalPayment ? (
              <PayPalPaymentButton
                className={s.buttonWrapper}
                buttonClassName={s.button}
                title={messages.buttonUseTitle}
                paymentType={paymentType}
                payPalBeginAction={payPalBeginAction}
                payPalCommitAction={payPalCommitAction}
                dataTestId={constants.testIds.payment.useAddressButton}
                onSuccess={payPalOnSuccess}
                onClick={this.handleClickUsePayPal}
              />
            ) : (
              <Button
                title={messages.buttonUseTitle}
                dataTestId={constants.testIds.payment.useAddressButton}
                weight="medium"
                color="brand"
                size="md"
                uppercase
                fullWidthOnMobile
                onClick={this.handleClickUse}
              />
            )
          }
          <Media mobile>
            {
              (isMobile) => (
                <Box {...isMobile ? { mt: '12' } : { ml: '12', inline: true }}>
                  <Button
                    title={messages.buttonEditTitle}
                    dataTestId={constants.testIds.payment.editAddressButton}
                    weight="medium"
                    color="brandBorder"
                    size="md"
                    uppercase
                    fullWidthOnMobile
                    onClick={this.handleClickEdit}
                  />
                </Box>
              )
            }
          </Media>
        </Box>
      </Box>
    )
  }
}


export default compose(
  injectGlobals(({ getCookieValue, getFeatureToggle }) => ({
    planName: (getCookieValue(constants.cookieNames.reactivateSubscription) || {}).planName,
    userGenderCookie: getCookieValue(constants.cookieNames.userGender),
    // TODO ducatape for GiftSubscriptionPaymentPage - there is gift data that we need pass to begin()
    giftSubscriptionCookie: getCookieValue(constants.cookieNames.payPalPaymentGiftSubscription),
    // TODO ducatape for GiftCardPaymentPage - there is gift data that we need pass to begin()
    giftCardCookie: getCookieValue(constants.cookieNames.payPalGiftCard),
    isNewPaymentFlowWithCreditsEnabled: getFeatureToggle(constants.features.newPaymentFlowWithCredits),
    isRegisterGqlEnabled: getFeatureToggle(constants.gqlFeatures.registerGQL),
  })),
  connect({
    paymentMethod: 'models.payment.paymentOption',
    // TODO move on top level
    plan: (state, props) => {
      const { view: { paymentPage: { selectedPlan } } } = state
      const { planName } = props

      return planName || selectedPlan
    },
    mainPageLink: (state, { userGenderCookie }) => getMainPageLink({ state, userGenderCookie }),
    isEnoughCreditsToBuyEcommerce: createSelector(
      (state, props) => props.isNewPaymentFlowWithCreditsEnabled,
      (state) => state.view.paymentPage,
      (isNewPaymentFlowWithCreditsEnabled, paymentPage) => {
        const { isEcommerceExist, isEnoughEcommerceCreditsToBuy } = paymentPage

        return isEcommerceExist && isEnoughEcommerceCreditsToBuy && isNewPaymentFlowWithCreditsEnabled
      },
    ),
  }),
  reduxForm(getFormOptions),
)(AddressesForm)
